import { useState } from 'react';
import styled from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Loader } from '@shared/ui/Progress';
import {
  useDispatchContractRisksMutation,
  useListContractRiskDispatchesQuery,
  useListMailboxRoutesQuery,
} from '@shared/api';

/**
 * Модалка «Разослать риски по отделам» на карточке договора. Юрист отмечает
 * ящики отделов (mailbox_routes диспетчера) и запускает рассылку: backend по
 * рискам договора строит для каждого отдела свой Word-документ, шлёт по email
 * и адресно уведомляет сотрудников роли отдела в приложении.
 */

const Intro = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(1)};
`;

const Row = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font-size: 13px;
  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }

  input {
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

const RouteMeta = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

const NoteArea = styled.textarea`
  width: 100%;
  min-height: 60px;
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  resize: vertical;
`;

const HistoryBlock = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding-top: ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const HistoryTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const HistoryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  font-size: 12px;
`;

const HistoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
  padding: ${({ theme }) => theme.spacing(3)} 0;
`;

const ErrorText = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

const SuccessText = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  line-height: 1.5;
`;

const Actions = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const RESPONSE_STATUS_LABELS: Record<string, string> = {
  pending: 'Ожидает ответа',
  accepted: 'Принято к сведению',
  mitigated: 'Учтено в работе',
  disputed: 'Есть возражения',
  objections: 'Есть возражения',
  commented: 'Прокомментировано',
};

interface Props {
  contractId: string;
  open: boolean;
  onClose: () => void;
}

export function DispatchRisksModal({ contractId, open, onClose }: Props) {
  const { data, isLoading } = useListMailboxRoutesQuery(undefined, { skip: !open });
  const { data: dispatchesData } = useListContractRiskDispatchesQuery(contractId, {
    skip: !open,
  });
  const [dispatchRisks, { isLoading: isSending }] = useDispatchContractRisksMutation();
  const [selected, setSelected] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const routes = (data?.items ?? []).filter((r) => r.is_active);
  const dispatches = dispatchesData?.items ?? [];

  function toggle(id: string) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function close() {
    setSelected([]);
    setComment('');
    setError(null);
    setSuccess(null);
    onClose();
  }

  async function onSubmit() {
    if (selected.length === 0) return;
    setError(null);
    try {
      await dispatchRisks({
        contractId,
        route_ids: selected,
        comment: comment.trim() || null,
      }).unwrap();
      setSuccess(
        `Рассылка запущена для ${selected.length} отдел(ов). Документы формируются и ` +
          'отправляются в фоне — сотрудники получат письмо и уведомление в приложении.',
      );
      setSelected([]);
      setComment('');
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось запустить рассылку. Попробуйте ещё раз.';
      setError(msg);
    }
  }

  return (
    <Modal open={open} title="Разослать риски по отделам" onClose={close}>
      <Intro>
        Отметьте отделы-получатели. Для каждого будет сформирован свой Word-документ:
        сверху выделены риски именно его компетенции, ниже — остальные для сведения.
        Документ уйдёт на почту отдела и придёт сотрудникам в приложении.
      </Intro>

      {isLoading ? (
        <Loader />
      ) : routes.length === 0 ? (
        <Empty>
          Нет настроенных ящиков отделов. Добавьте их в разделе «Диспетчер → Маршруты
          отделов».
        </Empty>
      ) : (
        <List>
          {routes.map((r) => (
            <Row key={r.id}>
              <input
                type="checkbox"
                checked={selected.includes(r.id)}
                onChange={() => toggle(r.id)}
              />
              <span>
                {r.display_name}
                {r.department ? ` · ${r.department}` : ''}
                <br />
                <RouteMeta>{r.email}</RouteMeta>
              </span>
            </Row>
          ))}
        </List>
      )}

      <NoteArea
        placeholder="Комментарий отделам (необязательно)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />

      {error && <ErrorText role="alert">{error}</ErrorText>}
      {success && <SuccessText role="status">{success}</SuccessText>}

      {dispatches.length > 0 && (
        <HistoryBlock>
          <HistoryTitle>Обратная связь от отделов ({dispatches.length})</HistoryTitle>
          {dispatches.map((d) => {
            const status = d.response_status ?? 'pending';
            const tone =
              status === 'accepted' || status === 'mitigated'
                ? 'success'
                : status === 'disputed' || status === 'objections'
                  ? 'warning'
                  : 'neutral';
            return (
              <HistoryRow key={d.id}>
                <HistoryHeader>
                  <strong>{d.department ?? d.email}</strong>
                  <Badge $tone={tone}>
                    {RESPONSE_STATUS_LABELS[status] ?? status}
                  </Badge>
                </HistoryHeader>
                <RouteMeta>
                  Профильных рисков: {d.focus_count} ·{' '}
                  {new Date(d.created_at).toLocaleString('ru-RU')}
                </RouteMeta>
                {d.response_comment && (
                  <div>Комментарий отдела: «{d.response_comment}»</div>
                )}
              </HistoryRow>
            );
          })}
        </HistoryBlock>
      )}

      <Actions>
        <Button type="button" $variant="ghost" onClick={close}>
          {success ? 'Закрыть' : 'Отмена'}
        </Button>
        {!success && (
          <Button
            type="button"
            onClick={onSubmit}
            disabled={isSending || selected.length === 0}
          >
            {isSending ? 'Запуск…' : `Разослать (${selected.length})`}
          </Button>
        )}
      </Actions>
    </Modal>
  );
}

